import { formatCurrency } from '@atc/string-fns';
import { memo, useContext, useCallback } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { Accordion, AccordionPanel, Box, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { ndsOfferBreakDownClick } from 'reaxl-analytics-handlers';
import BreakdownLineItem from './BreakdownLineItem.js';
import ConfigContext from '../ConfigContext/ConfigContext.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
function renderFinanceTerms(_ref) {
  let {
    rate,
    term,
    showSpecialAPR
  } = _ref;
  if (showSpecialAPR) {
    return `Special ${rate}% APR for ${term} mo.`;
  }
  return `${rate}% APR for ${term} mo.`;
}

/**
 * Renders the discount information section with MSRP, manufacturer offers, and dealer adjustments
 * @param {Object} params - The parameters object
 * @param {boolean} params.showMsrp - Flag to control MSRP visibility in the breakdown
 * @param {number} params.msrp - Manufacturer's Suggested Retail Price
 * @param {number} params.manufacturerOffer - Value of manufacturer's discount or incentive
 * @param {number} params.offersUpgrades - Value of dealer adjustments or upgrades (positive for upgrades, negative for discounts)
 * @param {boolean} params.enableOfferUpgrades - Flag to control visibility of dealer adjustments section
 * @param {boolean} params.disableIncentives - Flag to control visibility of manufacturer incentives section
 * @returns {React.ReactElement|null} The rendered discount information section or null if no discount data is available
 */
function renderDiscountInfo(_ref2) {
  let {
    showMsrp,
    msrp,
    manufacturerOffer,
    offersUpgrades,
    enableOfferUpgrades,
    disableIncentives
  } = _ref2;
  if (!msrp && !manufacturerOffer && !offersUpgrades) return null;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [showMsrp && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "MSRP",
      value: msrp
    }), !disableIncentives && !!manufacturerOffer && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "Manufacturer Offer",
      value: manufacturerOffer,
      priceIndicator: "-"
    }), enableOfferUpgrades && !!offersUpgrades && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: offersUpgrades < 0 ? 'Dealer Adjustment' : 'Dealer Discount',
      value: Math.abs(offersUpgrades),
      priceIndicator: offersUpgrades < 0 ? '+' : '-'
    }), /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-3"
    })]
  });
}

/**
 * Renders the section showing amounts due in advance including fees, taxes, and payments
 * @param {Object} params - The parameters object
 * @param {number} params.netDownPayment - Total amount customer needs to bring at signing
 * @param {number} params.diaFee - Sum of all applicable dealer, documentation, and administrative fees
 * @param {number} params.diaTax - Sum of all applicable sales tax, title tax, and other taxes
 * @param {number} params.firstPayment - Amount of first month's payment
 * @param {number} params.totalDueAtSigning - Total amount required at signing after all calculations
 * @param {string} params.isEstimate - Determines if the 'Est." label is shown
 * @returns {React.ReactElement} The rendered section showing itemized amounts due in advance
 */
function renderDueInAdvanceInfo(_ref3) {
  let {
    netDownPayment,
    diaFee,
    diaTax,
    firstPayment,
    totalDueAtSigning,
    isEstimate,
    isDueAtSigningAmountDeduct = false,
    cashDownLabelDS = null
  } = _ref3;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("li", {
      className: "display-flex justify-content-between align-items-center margin-bottom-1 text-subdued",
      children: /*#__PURE__*/jsx(Text, {
        weight: "bold",
        size: 300,
        children: "Due at Signing"
      })
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: isEstimate ? 'Est. Fees' : 'Fees',
      value: diaFee,
      priceIndicator: "+",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: isEstimate ? 'Est. Taxes' : 'Taxes',
      value: diaTax,
      priceIndicator: "+",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "First Months Payment",
      value: firstPayment,
      priceIndicator: "+",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: !isDueAtSigningAmountDeduct && cashDownLabelDS ? cashDownLabelDS : 'Down Payment',
      value: netDownPayment,
      priceIndicator: "-",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "Total Due at Signing",
      value: totalDueAtSigning,
      textBold: true,
      hasDecimals: true
    }), /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-3"
    })]
  });
}

/**
 * Renders the confirmation section of the breakdown showing finance or lease details
 * @param {Object} params - The parameters object
 * @param {boolean} params.isFinance - Flag indicating if this is a finance deal
 * @param {boolean} params.isLease - Flag indicating if this is a lease deal
 * @param {boolean} params.isCash - Flag indicating if this is a cash deal
 * @param {number} params.totalFinancing - Total amount being financed
 * @param {number} params.adjustedCapCost - Adjusted capitalized cost for lease calculations
 * @param {number} params.totalCashOut - Total cash payment required for cash deals
 * @param {number} params.financePayment - Monthly payment for finance deals
 * @param {number} params.leasePayment - Monthly payment for lease deals
 * @param {string} params.lender - Name of financing institution providing the loan or lease
 * @param {Object} params.paymentsInfo - Object containing payment terms and conditions
 * @param {number} params.paymentsInfo.loanTerm - Duration of the loan in months
 * @param {number} params.paymentsInfo.leaseTerm - Duration of the lease in months
 * @param {boolean} params.paymentsInfo.showSpecialAPR - Whether this is a special APR offer
 * @param {number} params.financeRate - Annual Percentage Rate for financing as a percentage
 * @returns {React.ReactElement|null} The rendered confirmation section or null if no applicable data
 */
function renderBreakdownConfirmation(_ref4) {
  let {
    isFinance,
    isLease,
    isCash,
    totalFinancing,
    adjustedCapCost,
    totalCashOut,
    financePayment,
    leasePayment,
    lender,
    paymentsInfo,
    financeRate
  } = _ref4;
  if (isFinance && !!totalFinancing && isLease && !!adjustedCapCost && isCash && !!totalCashOut) return null;
  return /*#__PURE__*/jsxs(Box, {
    backgroundColor: "brand-primary",
    children: [isFinance && !!totalFinancing && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(BreakdownLineItem, {
        textBold: true,
        label: "Finance Amount",
        value: totalFinancing > 0 ? totalFinancing : 0,
        hasDecimals: true
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Estimated Monthly Payment",
        value: Math.trunc(financePayment),
        size: 100,
        valueSuffix: "/mo."
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: lender,
        valueSuffix: renderFinanceTerms({
          term: paymentsInfo?.loanTerm,
          rate: financeRate,
          showSpecialAPR: paymentsInfo?.showSpecialAPR
        }),
        size: 100
      })]
    }), isLease && !!adjustedCapCost && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(BreakdownLineItem, {
        textBold: true,
        label: "Est. Adjusted Cap Cost",
        value: adjustedCapCost > 0 ? adjustedCapCost : 0,
        hasDecimals: true
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Estimated Monthly Payment",
        value: Math.trunc(leasePayment),
        valueSuffix: "/mo.",
        size: 100
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Lease Terms",
        valueSuffix: `for ${paymentsInfo?.leaseTerm} mo.`,
        size: 100
      })]
    }), isCash && !!totalCashOut && /*#__PURE__*/jsx(BreakdownLineItem, {
      textBold: true,
      label: "Estimated Cash Payment",
      value: totalCashOut > 0 ? totalCashOut : 0,
      hasDecimals: true
    })]
  });
}

/**
 * A component that displays a detailed breakdown of a vehicle offer
 * with support for financing, leasing, and cash payment options. It shows a complete
 * cost structure including MSRP, discounts, trade-in values, taxes, and fees.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {number} props.adjustedCapCost - Adjusted capitalized cost for lease calculations
 * @param {number} props.amountOwed - Outstanding balance on trade-in vehicle
 * @param {boolean} [props.autoOpen=false] - Whether to expand the breakdown accordion by default
 * @param {boolean} [props.disableIncentives=false] - Whether to hide manufacturer incentives section
 * @param {boolean} props.disableTrade - Whether to hide trade-in related information
 * @param {number} props.downPayment - Initial down payment amount
 * @param {number} props.dueAtSigningValue - Total amount required at contract signing
 * @param {boolean} [props.enableOfferUpgrades=true] - Whether to show dealer adjustments section
 * @param {boolean} [props.enablePriceBreakdown=true] - Whether to show detailed price structure breakdown
 * @param {number} props.financePayment - Monthly financing payment amount
 * @param {Function} props.getPricingBreakdown - Function to retrieve pricing details by payment type
 * @param {number} props.leasePayment - Monthly lease payment amount
 * @param {string} props.lender - Name of financing institution providing the loan or lease
 * @param {number} props.listingId - Unique identifier for vehicle listing
 * @param {Object} props.paymentsInfo - Details about payment terms and conditions
 * @param {number} props.financeRate - Annual Percentage Rate for financing as a percentage
 * @param {Function} props.renderTaxesAndFees - Function to render customized tax and fee information
 * @param {boolean} [props.showMsrp=true] - Whether to display MSRP in the breakdown
 * @param {string} [props.tabName='finance'] - Active payment type tab ('finance', 'lease', 'cash')
 * @param {number} props.totalCashOut - Total cash payment required for cash deals
 * @param {number} props.totalFinancing - Total amount being financed
 * @param {number} props.tradeInValue - Value of trade-in vehicle
 * @param {string} [props.uiContext='default'] - UI context for rendering
 * @param {number} props.diaFee - Sum of all applicable fees
 * @param {number} props.diaTax - Sum of all applicable taxes
 * @param {number} props.firstPayment - Amount of first payment
 * @param {number} props.cashDueFromCustomer - Total cash amount the customer needs to bring at signing (includes down payment, fees, taxes, and first payment)
 * @param {string} params.isEstimate - Determines if the 'Est." label is shown
 * @param {number} props.capitalizedTaxesAndFees - Amount of capitalized taxes and fees for lease calculations
 * @returns {React.ReactElement|null} The offer breakdown component or null if no pricing data
 */
function OfferBreakdown(_ref5) {
  let {
    adjustedCapCost,
    amountOwed,
    autoOpen = false,
    disableIncentives = false,
    disableTrade,
    downPayment,
    enableOfferUpgrades = true,
    enablePriceBreakdown = true,
    financePayment,
    getPricingBreakdown,
    leasePayment,
    lender,
    listingId,
    paymentsDuck,
    paymentsInfo,
    financeRate,
    renderTaxesAndFees,
    showMsrp = true,
    tabName = 'finance',
    totalCashOut,
    totalFinancing,
    tradeInValue,
    uiContext = 'deafult',
    diaFee,
    diaTax,
    firstPayment,
    cashDueFromCustomer,
    isEstimate,
    capitalizedTaxesAndFees
  } = _ref5;
  const {
    isDueAtSigningAmountDeduct = false,
    oemLabels = {}
  } = useContext(ConfigContext) ?? {};
  const {
    cashDownLabelDS
  } = oemLabels;
  const pricingDetail = getPricingBreakdown(tabName);
  const isBreakdownConfirmation = uiContext === 'confirmation';
  const {
    listingPrice,
    manufacturerOffer,
    msrp,
    offerPrice,
    offersUpgrades
  } = pricingDetail;
  const finalPrice = offerPrice ?? listingPrice;
  const netDownPayment = cashDueFromCustomer - (diaFee + diaTax + firstPayment);
  const totalDueAtSigning = cashDueFromCustomer;
  const isFinance = tabName === 'finance';
  const isLease = tabName === 'lease';
  const isCash = tabName === 'cash';
  const onOfferBreakDownOpen = useCallback(event => {
    sendClick(ndsOfferBreakDownClick, event, {
      tabName,
      listingId,
      walletData: {
        paymentsInfo,
        price: listingPrice,
        offerPrice
      },
      paymentsDuck
    });
  }, [tabName, listingId, paymentsInfo, listingPrice, offerPrice, paymentsDuck]);
  const renderPriceInfo = useCallback(() => /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(BreakdownLineItem, {
      label: offerPrice ? 'My Offer' : 'Listing Price',
      value: finalPrice
    }), isFinance && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: cashDownLabelDS ?? 'Down Payment',
      value: downPayment,
      priceIndicator: "-"
    }), !disableTrade && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Trade-In Value",
        value: tradeInValue || 0,
        priceIndicator: "-"
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Amount Owed on Trade-In",
        value: amountOwed || 0,
        priceIndicator: "+"
      })]
    }), tabName !== 'lease' && renderTaxesAndFees(tabName, 'offerBreakdown'), /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-3"
    })]
  }), [offerPrice, finalPrice, isFinance, downPayment, disableTrade, tradeInValue, amountOwed, tabName, renderTaxesAndFees, cashDownLabelDS]);
  const renderPriceBreakdown = useCallback(() => /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("ul", {
      className: "list-unstyled",
      children: [enablePriceBreakdown && renderDiscountInfo({
        showMsrp,
        msrp,
        manufacturerOffer,
        offersUpgrades,
        enableOfferUpgrades,
        disableIncentives
      }), renderPriceInfo(), isFinance && !!totalFinancing && !isBreakdownConfirmation && /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Amount Financed",
        value: totalFinancing > 0 ? totalFinancing : 0,
        textBold: true,
        hasDecimals: true
      }), isCash && !!totalCashOut && !isBreakdownConfirmation && /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Est. Cash Payment",
        value: totalCashOut > 0 ? totalCashOut : 0,
        textBold: true,
        hasDecimals: true
      })]
    }), isBreakdownConfirmation && renderBreakdownConfirmation({
      isFinance,
      isLease,
      isCash,
      totalFinancing,
      adjustedCapCost,
      totalCashOut,
      financePayment,
      leasePayment,
      lender,
      paymentsInfo,
      financeRate
    })]
  }), [enablePriceBreakdown, showMsrp, msrp, manufacturerOffer, offersUpgrades, enableOfferUpgrades, disableIncentives, renderPriceInfo, isFinance, totalFinancing, isBreakdownConfirmation, isCash, totalCashOut, isLease, adjustedCapCost, financePayment, leasePayment, lender, paymentsInfo, financeRate]);
  const renderLeaseBreakdown = useCallback(() => /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("ul", {
      className: "list-unstyled",
      children: [enablePriceBreakdown && renderDiscountInfo({
        showMsrp,
        msrp,
        manufacturerOffer,
        offersUpgrades,
        enableOfferUpgrades,
        disableIncentives
      }), renderPriceInfo(), renderDueInAdvanceInfo({
        diaFee,
        diaTax,
        firstPayment,
        totalDueAtSigning,
        isEstimate,
        netDownPayment,
        isDueAtSigningAmountDeduct,
        cashDownLabelDS
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: isEstimate ? 'Est. Taxes & Fees' : 'Taxes & Fees',
        value: capitalizedTaxesAndFees,
        hasDecimals: true,
        isInfoIcon: true,
        infoIconText: "Usually includes sales tax, DMV fees, any lender fees for financing, plus dealer-related fees for doc and prep",
        className: "margin-bottom-3"
      }), !!adjustedCapCost && !isBreakdownConfirmation && /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Est. Adjusted Cap Cost",
        value: adjustedCapCost > 0 ? adjustedCapCost : 0,
        textBold: true,
        hasDecimals: true
      })]
    }), isBreakdownConfirmation && renderBreakdownConfirmation({
      isLease,
      adjustedCapCost,
      leasePayment,
      paymentsInfo
    })]
  }), [enablePriceBreakdown, showMsrp, msrp, manufacturerOffer, offersUpgrades, enableOfferUpgrades, disableIncentives, renderPriceInfo, diaFee, diaTax, firstPayment, cashDueFromCustomer, adjustedCapCost, isBreakdownConfirmation, isLease, leasePayment, paymentsInfo, isEstimate, totalDueAtSigning, netDownPayment, capitalizedTaxesAndFees, cashDownLabelDS, isDueAtSigningAmountDeduct]);
  if (!pricingDetail || _isEmpty(pricingDetail)) {
    return null;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isBreakdownConfirmation && /*#__PURE__*/jsx(Accordion, {
      className: "padding-bottom-4",
      "data-cmp": "offerBreakdown",
      defaultActiveKey: autoOpen ? 'offer-breakdown-panel' : undefined,
      onOpen: onOfferBreakDownOpen,
      children: /*#__PURE__*/jsxs(AccordionPanel, {
        title: "Offer Breakdown",
        subTitle: formatCurrency(finalPrice),
        eventKey: "offer-breakdown-panel",
        withBorder: true,
        iconPlacement: "before",
        children: [isLease && renderLeaseBreakdown(), !isLease && renderPriceBreakdown()]
      })
    }), isBreakdownConfirmation && /*#__PURE__*/jsxs(Fragment, {
      children: [isLease && renderLeaseBreakdown(), !isLease && renderPriceBreakdown()]
    })]
  });
}
var OfferBreakdown$1 = /*#__PURE__*/memo(OfferBreakdown);
export { OfferBreakdown$1 as default };
